import { isNativeApp, pickNativeImageDetailed } from 'common/webview';

/** 사진 고르기 결과. 실패는 이유를 함께 돌려줍니다 */
export type PhotoPick =
    | { ok: true; file: File }
    | { ok: false; reason: PhotoPickFail };

export type PhotoPickFail =
    /** 네이티브가 아님 — 부르는 쪽이 파일 입력으로 가야 합니다 */
    | 'notNative'
    /** 사용자가 그만둠 — 실패가 아니므로 아무 말도 하지 않습니다 */
    | 'cancelled'
    /** 네이티브가 60초 안에 답하지 않음 */
    | 'timeout'
    /** 사진을 받았지만 내용이 비어 있음 */
    | 'noData'
    /** 받은 데이터를 파일로 만들지 못함 */
    | 'decodeFailed';

/**
 * base64 를 파일로. 데이터 URL 로 주는 기기가 있어 접두사를 떼어 냅니다 —
 * 떼지 않으면 atob 가 쉼표에서 예외를 던집니다.
 */
const base64ToFile = (raw: string, name: string, mime: string): File => {
    const b64 = raw.includes(',') ? raw.slice(raw.indexOf(',') + 1) : raw;
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
    if (bytes.length === 0) throw new Error('empty base64');
    return new File([bytes], name, { type: mime });
};

/**
 * 사진 한 장 고르기 — 네이티브 갤러리를 직접 엽니다.
 *
 * 웹뷰의 <input type="file"> 은 안드로이드에서 문서 선택기("파일")로 열려,
 * 사진을 고르러 갔는데 파일 탐색기가 나옵니다.
 *
 * **부르는 쪽 주의**: 이 함수를 await 한 뒤에 input.click() 으로 넘어갈 수
 * 없습니다. 그 시점에는 사용자 제스처가 끝나 웹뷰가 클릭을 무시합니다.
 * 네이티브인지는 isNativeApp() 으로 **동기로 먼저** 가르십시오.
 */
export const pickPhotoFile = async (): Promise<PhotoPick> => {
    if (!isNativeApp()) return { ok: false, reason: 'notNative' };

    const res = await pickNativeImageDetailed('gallery');
    if (res.cancelled) return { ok: false, reason: 'cancelled' };
    if (res.timedOut) return { ok: false, reason: 'timeout' };

    const img = res.image;
    if (!img) return { ok: false, reason: res.error ? 'decodeFailed' : 'cancelled' };

    const name = img.filename || img.fileName || 'photo.jpg';
    const mime = img.mime || img.type || 'image/jpeg';

    /* 네이티브가 base64 를 함께 줍니다(includeBase64). 이 길이 정상 경로입니다. */
    if (img.base64) {
        try {
            return { ok: true, file: base64ToFile(img.base64, name, mime) };
        } catch {
            return { ok: false, reason: 'decodeFailed' };
        }
    }

    /* base64 가 없을 때만 uri 를 읽어 봅니다. 안드로이드의 file:// · content:// 는
       웹뷰가 fetch 하지 못하는 경우가 많아, 실패를 조용히 넘기지 않습니다. */
    if (img.uri) {
        try {
            const blob = await (await fetch(img.uri)).blob();
            if (blob.size === 0) return { ok: false, reason: 'noData' };
            return { ok: true, file: new File([blob], name, { type: blob.type || mime }) };
        } catch {
            return { ok: false, reason: 'decodeFailed' };
        }
    }
    return { ok: false, reason: 'noData' };
};

/** 사용자에게 말해 줘야 하는 실패인가 — 취소와 웹 환경은 조용히 넘깁니다 */
export const shouldTellUser = (reason: PhotoPickFail): boolean =>
    reason !== 'cancelled' && reason !== 'notNative';
